import { useEffect, useMemo, useState } from 'react';
import {
  CarouselProvider,
  Dot,
  Image,
  Slide,
  Slider
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import cx from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Product } from '../../../models/Product/Product';
import { Media } from '../../../models/Media/Media';
import collect, { Collection } from 'collect.js';
import {
  ProductAttribute,
  ProductStatus,
  ProductType
} from '../../../models/Product/contracts';
import style from './single-product.module.scss';
import AttributeGroup from '../../../Shop/components/Attributes/AttributeGroup';
import ShopSideBar from '../../components/SideBar/ShopSideBar';
import { useCartContext } from '../../../context/CartContext/CartContext';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

// --- Komponente za varijabilne i proste proizvode (Vaša originalna logika) ---
interface ProductDetailsProps {
  product: Product;
  setSelectedProduct: (product: Product) => void;
}

function VariableProduct({ product, setSelectedProduct }: ProductDetailsProps) {
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<{ [key: string]: number; }>({});

  const attributes = useMemo(
    () =>
      collect(product.attributes ?? [])
        .groupBy<Collection<ProductAttribute>, string>('name')
        .map<ProductAttribute[]>((attributes) => attributes.toArray()),
    [product.attributes]
  );

  const variations = useMemo(() => {
    const variationMap = new Map<string, Product>();
    product?.variations?.forEach((variation) => {
      const key =
        variation.attributes
          ?.map(({ id }) => id)
          .sort((a, b) => a - b)
          .join('-') ?? '';
      if (key) {
        variationMap.set(key, variation);
      }
    });
    return variationMap;
  }, [product?.variations]);

  useEffect(() => {
    if (Object.keys(selectedAttributeValues).length === 0) return;
    const key = Object.values(selectedAttributeValues)
      .sort((a, b) => a - b)
      .join('-');
    const selectedVariation = variations.get(key);
    if (selectedVariation) {
      setSelectedProduct(selectedVariation);
    }
  }, [selectedAttributeValues, variations, setSelectedProduct]);

  return (
    <div className={style.variableProductOptions}>
      {attributes
        .map((attributes, key) => (
          <AttributeGroup
            key={key}
            title={key}
            attributes={attributes}
            isCard={false}
            onSelect={({ name, id }) => {
              setSelectedAttributeValues((currentValues) => ({
                ...currentValues,
                [name]: id
              }));
            }}
          />
        ))
        .toArray()}
    </div>
  );
}

function SimpleProduct({ product, setSelectedProduct }: ProductDetailsProps) {
  useEffect(() => {
    setSelectedProduct(product);
  }, [product, setSelectedProduct]);
  return null;
}

function ProductDetailsFactory({ product, ...props }: ProductDetailsProps) {
  switch (product?.type) {
    case ProductType.SIMPLE:
      return <SimpleProduct product={product} {...props} />;
    case ProductType.VARIABLE:
      return <VariableProduct product={product} {...props} />;
    default:
      return null;
  }
}

// --- Glavna komponenta stranice ---
type SingleProductProps = {
  collapsedSidebar: boolean;
  setCollapsedSidebar: (collapsed: boolean | ((prevState: boolean) => boolean)) => void;
};

function SingleProduct({ collapsedSidebar, setCollapsedSidebar }: SingleProductProps) {
  const { id } = useParams<{ id: string }>();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { cart, cartAddProduct, cartRemoveProduct } = useCartContext();

  const {
    data: product,
    isLoading,
    isError,
  } = useQuery(['product', id], () => Product.get(id!), {
    enabled: !!id,
    onSuccess: (data) => {
      setSelectedProduct(data);
    },
  });

  const gallery = useMemo(() => {
    if (!product) return [];
    const cover = product.coverImage ? new Media(product.coverImage) : undefined;
    const galleryList = Array.isArray(product.gallery)
      ? product.gallery.map((img: any) => (img instanceof Media ? img : new Media(img)))
      : [];
    if (cover && !galleryList.some((img) => img.id === cover.id)) {
      return [cover, ...galleryList];
    }
    return galleryList;
  }, [product]);

  // Vaša originalna logika za grupisanje
  const attributesGrouped = useMemo(() => {
    if (!selectedProduct?.attributes) return [];
    const map: Record<string, string[]> = {};
    selectedProduct.attributes.forEach((av) => {
      if (!map[av.name]) map[av.name] = [];
      map[av.name].push(av.value);
    });
    return Object.entries(map);
  }, [selectedProduct]);
  
  const featuresGrouped = useMemo(() => {
    if (!selectedProduct?.features) return [];
    const map: Record<string, string[]> = {};
    selectedProduct.features.forEach((fv) => {
      if (!map[fv.name]) map[fv.name] = [];
      map[fv.name].push(fv.value);
    });
    return Object.entries(map);
  }, [selectedProduct]);

  const isProductInCart = useMemo(() => {
    if (!selectedProduct || !cart?.products) return false;
    return cart.products.some((p) => p.id === selectedProduct.id);
  }, [selectedProduct, cart?.products]);

  const isAvailable = selectedProduct?.status === ProductStatus.AVAILABLE;

  const handleCartAction = async () => {
    if (!selectedProduct || !isAvailable) return;
    try {
      if (isProductInCart) {
        await cartRemoveProduct(selectedProduct.id);
        toast.success('Kostim je uspešno uklonjen iz liste.');
      } else {
        await cartAddProduct(selectedProduct.id, 1);
        toast.success('Kostim je uspešno dodat u listu.');
      }
    } catch (error) {
      console.error('Greška pri ažuriranju korpe:', error);
      toast.error('Došlo je do greške.');
    }
  };

  const calculatedPrice = useMemo(() => {
    if (!selectedProduct?.price) return 0;
    return selectedProduct.price * (cart?.priceFactor ?? 1);
  }, [selectedProduct?.price, cart?.priceFactor]);

  if (isLoading) {
    return <div className="cs-content">Učitavanje proizvoda...</div>;
  }

  if (isError || !product) {
    return <div className="cs-content">Proizvod nije pronađen.</div>;
  }

  return (
    <>
      <ShopSideBar
        collapsed={collapsedSidebar}
        setCollapsed={setCollapsedSidebar}
        heading="Dodatne informacije"
      >
        {selectedProduct?.features?.map((item: any) => (
          <div key={item.id} className={style.sidebarItem}>
            <div className={style.sidebarTitle}>{item.name}:</div>
            <div className={style.sidebarValue}>{item.value}</div>
          </div>
        ))}
      </ShopSideBar>
      <div className={cx('cs-content', style.pageWrapper)}>
        <div className={style.gallery}>
          <CarouselProvider
            naturalSlideWidth={3}
            naturalSlideHeight={4}
            totalSlides={gallery.length}
            currentSlide={currentSlide}
            visibleSlides={1}
            className={style.carouselWrapper}
          >
            <Slider className={style.mainSlider} aria-label="Glavna slika proizvoda">
              {gallery.map((item, index) => (
                <Slide index={index} key={item.id}>
                  <Image src={item.fullUrl} alt={`${product.name} - slika ${index + 1}`} hasMasterSpinner={false} />
                </Slide>
              ))}
            </Slider>
            <div className={style.thumbs}>
              {gallery.map((item, index) => (
                <Dot slide={index} key={`dot-${item.id}`} onClick={() => setCurrentSlide(index)} className={style.thumbItem}>
                  <img src={item.fullUrl} alt={`Thumbnail ${index + 1}`} />
                </Dot>
              ))}
            </div>
          </CarouselProvider>
        </div>

        <div className={style.info}>
          <h4 className={style.category}>{selectedProduct?.category?.name}</h4>
          <h1 className={style.title}>{selectedProduct?.name}</h1>
          <p className={style.sku}>SKU: {selectedProduct?.sku}</p>

          <div className={style.priceSection}>
            <p className={style.price}>{calculatedPrice.toLocaleString()}&nbsp;RSD</p>
            {!isAvailable && <span className={style.statusTag}>Nije dostupno</span>}
          </div>
          
          <ProductDetailsFactory product={product} setSelectedProduct={setSelectedProduct} />

          <button className={style.addToCartButton} onClick={handleCartAction} disabled={!isAvailable}>
            {isProductInCart ? <FaTimes /> : <FaPlus />}
            <span>{isProductInCart ? 'Ukloni iz liste' : 'Dodaj na listu'}</span>
          </button>
          
          {/* AŽURIRANO: Novi prikaz za atribute i karakteristike */}
          <div className={style.detailsSection}>
            {attributesGrouped.length > 0 && (
              <>
                <h3>Atributi</h3>
                <ul className={style.detailsList}>
                  {attributesGrouped.map(([name, values]) => (
                    <li key={name}>
                      <strong>{name}:</strong>
                      <span>{values.join(', ')}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            
            {featuresGrouped.length > 0 && (
              <>
                <h3 className={style.featuresHeading}>Karakteristike</h3>
                <ul className={style.detailsList}>
                  {featuresGrouped.map(([name, values]) => (
                    <li key={name}>
                      <strong>{name}:</strong>
                      <span>{values.join(', ')}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SingleProduct;